import React, { createContext, useState, useContext, useEffect } from 'react';
import { onAuthStateChanged, signOut, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Clear legacy persistent login so old sessions are not restored
    localStorage.removeItem('mou_user');
    sessionStorage.removeItem('mou_user');

    if (!auth) {
      console.warn("Auth is not initialized. Check your Firebase config.");
      setLoading(false);
      return;
    }

    let unsubscribe = () => {};

    const initAuth = async () => {
      try {
        // This ensures the user is logged out when they close the browser tab/window
        await setPersistence(auth, browserSessionPersistence);
        
        unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          setUser(currentUser);
          setLoading(false);
        });
      } catch (error) {
        console.error("Auth initialization error:", error);
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    // Clear real Firebase sessions
    if (auth) {
      try {
        await signOut(auth);
      } catch (error) {
        console.error('Error signing out:', error);
      }
    }
    
    // Force reload to ensure all states and caches are cleared
    window.location.reload();
  };

  const value = {
    user,
    logout,
    isAuthenticated: !!user,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
